import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import './TermsAndConditionPage.css';

interface TermsAndConditionPageState {
  // Simple state matching reference project
}

interface TermsAndConditionPageProps extends WithTranslation {
  navigate?: (path: string) => void;
}

// Simple interface for terms and conditions content
interface TermsAndConditionContent {
  htmlContent: string;
}

class TermsAndConditionPage extends Component<TermsAndConditionPageProps, TermsAndConditionPageState> {
  constructor(props: TermsAndConditionPageProps) {
    super(props);
    this.state = {};
  }

  // Navigation method - Navigate back to settings
  goBack = () => {
    const { navigate } = this.props;
    if (navigate) {
      // Navigate back to settings page (reference project behavior)
      navigate('/settings');
    }
  };

  // Terms and Conditions Content - Single HTML variable for easy management
  // EASY MANAGEMENT:
  // 1. Edit HTML directly in this string for content updates
  // 2. Copy/paste from HTML editor or Word (save as HTML)
  // 3. Replace entire string with CMS/database content
  // 4. Use template literals for dynamic values: ${variable}
  //
  // FUTURE DYNAMIC OPTIONS:
  // 1. Redux: this.props.termsAndConditionContent?.htmlContent
  // 2. API: await this.props.getTermsAndConditionContent()
  // 3. CMS: fetch from admin panel
  // 4. File: import from separate .html file
  getTermsAndConditionContent = (): TermsAndConditionContent => {
    // Future: return { htmlContent: this.props.termsAndConditionContent?.htmlContent || this.getDefaultContent() };
    return {
      htmlContent: `
        <p class="last-updated">Last updated: 4 December 2018</p>
        
        <p>
          The Child Development Centre ("CDC") Terms and Conditions ("Terms") and Privacy Policy govern the use of the www.cdchk.org website, mobile application and all related content ("the Service") including but not limited to your participation in comments, subscriptions and purchasing. Please read the following information carefully before you continue using the Service. By using the Services, you are indicating that you have read and unconditionally accept the Terms below.
        </p>

        <h3>Changes to the Content and Terms</h3>
        <p>
          The CDC will occasionally amend the content of the Service including but not limited to the posts and the Terms to reflect colleague, client and other feedback. The effective date of the most recent Terms is noted at the top of this page. By continuing to use the Service, you signify that you have read and unconditionally accept the Terms as amended from time to time.
        </p>

        <h3>Compliance with the Law</h3>
        <p>
          The CDC complies with the Hong Kong Personal Data (Privacy) Ordinance. You may wish to visit the Office of the Privacy Commission for more information about this Ordinance.
        </p>

        <h3>Your Use of the Service</h3>
        <p>
          The CDC reserves all rights relating to the content of the Service, except the user-generated content, unless stated otherwise. CDC owns all copyright and other intellectual property rights to the content of the Service. For the avoidance of doubt, this includes the copyright in the software code of the App. Nothing in the provision of the Services amounts to a waiver of CDC's rights, nor a grant, license or assignment of any of CDCs rights to you or any third party. You may not copy, distribute, transmit, publicly display, reproduce, edit or publish any of the content of the Service except under and in accordance with the expressed permission of CDC.
        </p>
        <p>
          The Service is for your own personal use and not for any commercial use. Should you breach any of the Terms, the CDC reserves the right to terminate your use of the Service and any of its related content including purchasing and promotions.
        </p>
        <p>
          Any content that is deemed to be offensive, violent, inaccurate, incomplete, misleading or breaches any laws of the Hong Kong Special Administrative Region is strictly prohibited. The nature of such content shall be determined at CDC's sole discretion and CDC reserves the right to remove such content without notice.
        </p>

        <h3>Your Personal Information</h3>
        <p>
          Some sections of the Service invite you to request information or to contact the CDC with comments or questions. If you do so, the CDC will normally keep your email address and other details supplied by you on a database. We will not disclose any personal information you provide by means of the Service to third parties unconnected with CDC without your consent, except to our data processors or as otherwise permitted by the relevant data protection legislation. Addresses, email addresses and other personal details supplied by means of the Service will be used for the purposes for which you supply them such as marketing or as stated on the relevant section of the Service and in some cases, it may also be used for statistical analysis.
        </p>

        <h3>Notifications</h3>
        <p>
          The CDC may send you notifications through the Service, including via email, push notifications and other similar means, in accordance with the preferences you set through the Service. By providing your email address, you consent to the CDC using the email address to send you service-related notices. Email messages and any attachment(s) are private and confidential and are intended for the recipient(s) identified. If you have received a message by mistake, please immediately notify the sender and delete the message. You are not entitled to use or copy the message, or to disclose it to any third party. It is your choice whether you receive emails from us. If you wish to opt out of promotional emails, newsletter or updates from the CDC at any time please click the 'unsubscribe' link at the bottom of the email or contact the CDC via email at info@cdchk.org.
        </p>

        <h3>Donating</h3>
        <p>
          In order to donate to the CDC, you will need to provide your personal information and billing information as requested. All information and transactions are kept strictly confidential. If you are under 18 years of age, please help the CDC make sure that you have verifiable parental consent before donating. The CDC reserves the right to block content from underage users, and accept orders only from those over the age of 18.
        </p>

        <h3>Links</h3>
        <p>
          The links provided through the Service are for your convenience. Once you click the link and are directed to a third-party website, you are no longer governed by the CDC's Terms and the CDC is no longer responsible for any of the content these websites represent.
        </p>

        <h3>Disclaimers</h3>
        <p>
          The Service is provided to you on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind by The Child Development Centre (CDC), whether expressed, implied or otherwise. The CDC does not guarantee the Service will meet your requirements or will be available at any particular time or location, or will be uninterrupted or secure, or that any defects or errors will be corrected. The CDC expressly disclaims any liabilities or warranties of the accuracy, relevance, timeliness, completeness or fitness for a particular purpose. The use of the Service is at your own risk. Under no circumstances shall the CDC be liable to you or anyone else for any direct, indirect, special, incidental, or consequential damages of any kind for the use of the Service.
        </p>

        <h3>Questions about the Terms and Conditions</h3>
        <p style="padding-bottom: 50px;">
          If you have any questions about these Terms, please contact the CDC by email at info@cdchk.org
        </p>
      `
    };
  };

  render() {
    const { t } = this.props;
    const termsContent = this.getTermsAndConditionContent();

    return (
      <div className="terms-page">
        <div className="terms-container">
          {/* Header - matching reference project structure */}
          <div className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button className="header-button back-button" onClick={this.goBack}>
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">
                    {t('terms-and-condition-screen:title')}
                  </h1>
              </div>
            </div>
          </div>

          {/* Content - Single HTML variable for easy management */}
          <div 
            className="terms-content"
            dangerouslySetInnerHTML={{ __html: termsContent.htmlContent }}
          />
        </div>
      </div>
    );
  }
}

// Navigation wrapper component - matching reference project pattern
const TermsAndConditionPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <TermsAndConditionPage
      {...props}
      navigate={handleNavigate}
    />
  );
};

// Redux connection matching reference project
const mapStateToProps = (state: any) => {
  return {};
};

const mapDispatchToProps = (dispatch: any) => {
  return {};
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(TermsAndConditionPageWithNavigation));
